import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./CreateListModal.css";

function CreateListModal({ isOpen, onClose, onListCreated, availableLeads }) {
  const { getVerifiedJWT } = useAuth();
  const [listName, setListName] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setListName("");
      setSelectedLeads([]);
      setSearchTerm("");
      setError("");
    }
  }, [isOpen]);

  const handleToggleLead = leadId => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    const filteredLeadIds = filteredLeads.map(lead => lead.id);
    if (selectedLeads.length === filteredLeadIds.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeadIds);
    }
  };

  const filteredLeads = availableLeads.filter(lead => {
    const fullName = `${lead.first_name} ${lead.last_name}`.toLowerCase();
    const email = (lead.email || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");

    if (!listName.trim()) {
      setError("Please enter a list name");
      return;
    }

    setIsSubmitting(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication required");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(getApiEndpoint("/v2/recruiting/lists"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name: listName.trim(),
          lead_ids:
            selectedLeads.length > 0
              ? selectedLeads.map(id => parseInt(id))
              : undefined,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        onListCreated(result.data);
        onClose();
      } else {
        const result = await response.json();
        setError(result.message || "Failed to create list");
      }
    } catch (err) {
      setError("An error occurred while creating the list");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container create-list-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            <span className="material-icons">create_new_folder</span>
            Create New List
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="listName" className="form-label required">
                List Name
              </label>
              <input
                type="text"
                id="listName"
                className="form-input"
                placeholder="e.g., High Priority Recruits"
                value={listName}
                onChange={e => setListName(e.target.value)}
                maxLength={100}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Add Recruits (Optional)</label>
              <p className="form-helper-text">
                You can add recruits now or later
              </p>

              <div className="recruits-selection-container">
                <div className="recruits-search">
                  <span className="material-icons search-icon">search</span>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search recruits..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="recruits-list-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={
                        selectedLeads.length === filteredLeads.length &&
                        filteredLeads.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                    <span>
                      {selectedLeads.length > 0
                        ? `${selectedLeads.length} selected`
                        : "Select all"}
                    </span>
                  </label>
                </div>

                <div className="recruits-list">
                  {filteredLeads.length === 0 ? (
                    <div className="empty-recruits-state">
                      <span className="material-icons">people_outline</span>
                      <p>
                        {searchTerm
                          ? "No recruits found"
                          : "No recruits available"}
                      </p>
                    </div>
                  ) : (
                    filteredLeads.map(lead => (
                      <label key={lead.id} className="recruit-item">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleToggleLead(lead.id)}
                        />
                        <span className="recruit-name">
                          {lead.first_name} {lead.last_name}
                        </span>
                        <span className="recruit-status">{lead.status}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !listName.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Creating...
                </>
              ) : (
                <>
                  <span className="material-icons">add</span>
                  Create List
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateListModal;
